import axios, { AxiosResponse } from "axios";
import moment, { Moment } from "moment";
import { FieldData } from "rc-field-form/lib/interface";
import { OptionData, OptionGroupData } from "rc-select/lib/interface";
import {
  Account,
  BankAccountTypes,
  BeneficiaryTypes,
  BeneficiaryTypesEnum,
  BulkUploadSourceTypesEnum,
  BulkUploadStatusTypesEnum,
  Company,
  CompanySegments,
  CompanySegmentsEnum,
  ComplaintTypes,
  ComplaintsTypesEnum,
  ContactTypes,
  ContactTypesEnum,
  Contribution,
  ContributionTypes,
  ContributionTypesEnum,
  Customer,
  Entitlement,
  EntityException,
  ExceptionTypes,
  FileOption,
  FileTypes,
  ExceptionTypesEnum,
  GetUserTokenResponse,
  Greetings,
  GridColConfig,
  InfluenceLevelTypes,
  InfluenceLevelTypesEnum,
  NotificationCategoryTypes,
  NotificationCategoryTypesEnum,
  NotificationStatusTypes,
  NotificationStatusTypesEnum,
  NotificationType,
  NotificationTypesEnum,
  Opportunity,
  OpportunityProspect,
  PaymentAccount,
  PaymentAccountTypes,
  PaymentModeTypes,
  PaymentAccountTypesEnum,
  PaymentModeTypesEnum,
  PayoutVerificationFailure,
  Port,
  Product,
  ProductContributionSubscriptionMapping,
  ProductPaymentType,
  RedemptionRequestStates,
  RedemptionTypeEnum,
  RedemptionTypes,
  Scheme,
  SelectOptionsInterface,
  Subscription,
  SubscriptionTag,
  TiersEnum,
  TiersTypes,
  TransactionFieldsEnum,
  TransferRequest,
  TransferRequestStates,
  TransferTypeEnum,
  TransferTypes,
  UpdatePaymentInformationFnProps,
  User,
} from "./shared-interfaces";

import * as Sentry from "@sentry/browser";
import { message } from "antd";
import { IField } from "../components/AdvancedSearchModal";
import { APIError } from "./shared-interfaces";

export const dateFormat = "YYYY/MM/DD";

export const disabledDate = (current: Moment | undefined): boolean => {
  const eighteenYearsAgo = moment().subtract(18, "year");
  return Boolean(current && current.isAfter(eighteenYearsAgo));
};

export const dealInsertValidDates = (allowPast?: boolean) => {
  return (current: moment.Moment | undefined): boolean => {
    if (!current) return false;

    const today = moment().startOf("day");

    if (allowPast) {
      const min = moment("2012-01-01", "YYYY-MM-DD");
      return (
        current.isBefore(min, "day") || current.isSameOrAfter(today, "day")
      );
    }

    // disable all dates
    return true;
  };
};

export const getAPIUrl = (): string => {
  const apiUrl = localStorage.getItem("apiUrl");

  if (apiUrl != null) {
    return apiUrl;
  } else {
    return process.env.REACT_APP_API_URL as string;
  }
};

export const displayDefaultPickerValue = function (): Moment {
  const yearsBefore = moment().subtract(18, "year");
  return moment(yearsBefore, dateFormat);
};

export const getUniqueArrayObjects = function (arrayOfObjects: any[]): any[] {
  return arrayOfObjects.filter(
    (item, index) =>
      arrayOfObjects.findIndex((itemIndex) => item.id === itemIndex.id) ===
      index,
  );
};

export const getUniqueArrayObjectsByKey = function (
  arrayOfObjects: any[],
  key: string,
): any[] {
  return arrayOfObjects.filter(
    (item, index) =>
      arrayOfObjects.findIndex((itemIndex) => item[key] === itemIndex[key]) ===
      index,
  );
};

axios.interceptors.response.use(
  (response): AxiosResponse => {
    return response;
  },
  (error): Promise<void> | void => {
    if (error.response && error.response.status === 401) {
      localStorage.clear();
      window.location.pathname !== "/login" &&
        window.location.assign(
          `/login?redirect=${encodeURIComponent(
            window.location.pathname + window.location.search,
          )}`,
        );
    } else if (error.response && error.response.status === 403) {
      message.error({
        content:
          "You do not have all the required permissions to perform this action",
        key: "permission-denied",
      });
      return Promise.reject(error);
    } else {
      return Promise.reject(error);
    }
  },
);

export const request = (
  data: {
    query: string;
    variables?: Record<string, unknown>;
  },
  config?: { timeout: number },
): Promise<AxiosResponse> => {
  const token = localStorage.getItem("msal.idtoken");
  axios.defaults.headers = { Authorization: token || "" };

  return axios.post(getAPIUrl(), data, config);
};

export function createValidationErrorMessage(): string {
  return `This field is required`;
}

export function listContainsId(list: EntityException[], id: string): boolean {
  return list.some((e: EntityException): boolean => e.entityId === id);
}

export function checkExceptions({
  exceptions,
  user,
  id,
}: {
  exceptions: EntityException[];
  user: User;
  id: string;
}): boolean {
  if (!exceptions) return true;

  const exclusionsForUser: EntityException[] = [];
  const inclusionForUser: EntityException[] = [];

  exceptions.forEach((exception: EntityException): void => {
    if (exception.exceptionType === "exclusion") {
      exception.user?.id === user?.id && exclusionsForUser.push(exception);
    } else {
      exception.user?.id === user?.id && inclusionForUser.push(exception);
    }
  });

  if (inclusionForUser.length > 0) {
    return listContainsId(inclusionForUser, id);
  }

  if (exclusionsForUser.length > 0) {
    return !listContainsId(exclusionsForUser, id);
  }

  return true;
}

export function checkPermission(
  user: User | null,
  entitlement: string | undefined,
): {
  canAccess: boolean;
  entitlement?: Entitlement;
} {
  if (!entitlement) return { canAccess: false };
  if (!user) return { canAccess: false };

  const entitlements = user.entitlements;
  if (!entitlements) return { canAccess: false };
  for (const e of entitlements) {
    if (e.name === entitlement) {
      return { entitlement: e, canAccess: true };
    }
  }
  return { canAccess: false };
}

export function generateGreeting(hour: number): string {
  if (hour < 12) {
    return Greetings.morning;
  } else if (hour >= 12 && hour < 17) {
    return Greetings.afternoon;
  } else {
    return Greetings.evening;
  }
}

export const formatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "GHS",
});

export const unitFormatter = new Intl.NumberFormat("en-US", {
  style: "decimal",
  maximumFractionDigits: 4,
});

export function capitalizeHypenatedName(name: string): string {
  const transformed: string = name.replace(/(^|[\s-]|_)\S/g, function (match) {
    return match.toUpperCase();
  });
  return transformed;
}

export function capitalizeFirstLetter(word: string): string {
  if (!word) return "";
  return word.charAt(0).toUpperCase() + word.slice(1);
}

export function range(start: number, end: number): number[] {
  return new Array(end - start + 1)
    .fill(undefined)
    .map((_, i): number => i + start);
}

export const notificationStatusTypes: NotificationStatusTypes = {
  delivered: "Delivered",
  failed: "Failed",
};

export const notificationTypes: NotificationType = {
  email: "Email",
  sms: "SMS",
};

export const notificationCategoryTypes: NotificationCategoryTypes = {
  onboarding: "Onboarding",
  transfer_requests: "Transfer Requests",
};

export const productPaymentTypes: ProductPaymentType = {
  direct_debit: "Direct Debit",

  non_direct_debit: "Self-Payment",

  pre_tax: "Pre Tax",

  employer: "Employer deduction (Post Tax)",

  pre_and_post_tax: "Pre/Post Tax",

  post_tax: "Post Tax",
};

export const influenceLevelTypes: InfluenceLevelTypes = {
  low: "Low",
  medium: "Medium",
  high: "High",
};

export const beneficiaryTypes: BeneficiaryTypes = {
  institution: "Institution",

  individual: "Individual",

  minor: "Minor",
};

export const bankAccountTypes: BankAccountTypes = {
  savings_account: "Savings Account",
  current_account: "Current Account",
};

export const paymentAccountTypes: PaymentAccountTypes = {
  cheque: "Cheque",
  mm_account: "Mobile Money",
  bank_account: "Bank Account",
};

export const paymentModeTypes: PaymentModeTypes = {
  cheque: "Cheque",
  bank_transfer: "Bank Transfer",
  mobile_money: "Mobile Money",
};

export const contributionTypes: ContributionTypes = {
  lump_sum: "Lump Sum",
  monthly_contribution: "Monthly Contribution",
  monthly_and_lumpsum: "Monthly and Lump Sum",
};

export enum preTaxProducts {
  evergreen = "Evergreen Pension Scheme",
  paps = "Petra Advantage Pension Scheme",
}

export const tierTypes: TiersTypes = {
  tier_2: "Tier 2",
  tier_3: "Tier 3",
  tier_4: "Tier 4",
};

export const exceptionTypes: ExceptionTypes = {
  inclusion: "Inclusion",
  exclusion: "Exclusion",
};

export const transferTypes: TransferTypes = {
  transfer_in: "Transfer In",
  transfer_out: "Transfer Out",
  transfer_within: "Transfer Within",
  transfer_to_preservation: "Transfer To Preservation",
  transfer_to_ezhome: "Transfer To ezHomes",
};

export const contactTypes: ContactTypes = {
  phone: "Phone",
  email: "Email",
};

export const complaintTypes: ComplaintTypes = {
  notAvailable: "N/A",
  service: "Service",
  intermediary: "Intermediary",
  product: "Product",
};

export const redemptionTypes: RedemptionTypes = {
  percentage: "Percentage",
  amount: "Amount",
};

export const transferRequestStates: Partial<TransferRequestStates> = {
  completed: "Completed",
  pending: "Pending",
  cancelled: "Cancelled",
};

export const redemptionRequestStates: Partial<RedemptionRequestStates> = {
  pending: "Pending",
  cancelled: "Cancelled",
};

export const companySegments: CompanySegments = {
  strategic: "Strategic",
  growth: "Growth",
  maintenance: "Maintenance",
  niche: "Niche",
  red: "Red",
};

export function generateNotificationStatusTypes(): SelectOptionsInterface[] {
  return [
    {
      id: NotificationStatusTypesEnum.delivered,
      name: notificationStatusTypes.delivered,
      value: NotificationStatusTypesEnum.delivered,
    },
    {
      id: NotificationStatusTypesEnum.failed,
      name: notificationStatusTypes.failed,
      value: NotificationStatusTypesEnum.failed,
    },
  ];
}

export function generateNotificationTypes(): SelectOptionsInterface[] {
  return [
    {
      id: NotificationTypesEnum.email,
      name: notificationTypes.email,
      value: NotificationTypesEnum.email,
    },
    {
      id: NotificationTypesEnum.sms,
      name: notificationTypes.sms,
      value: NotificationTypesEnum.sms,
    },
  ];
}

export function generateNotificationCategoryTypes(): SelectOptionsInterface[] {
  return [
    {
      id: NotificationCategoryTypesEnum.onboarding,
      name: notificationCategoryTypes.onboarding,
      value: NotificationCategoryTypesEnum.onboarding,
    },
    {
      id: NotificationCategoryTypesEnum.transfer_requests,
      name: notificationCategoryTypes.transfer_requests,
      value: NotificationCategoryTypesEnum.transfer_requests,
    },
  ];
}

export function generateBulkUploadStatusTypes(): SelectOptionsInterface[] {
  return [
    {
      id: BulkUploadStatusTypesEnum.completed,
      name: "Completed",
      value: BulkUploadStatusTypesEnum.completed,
    },
    {
      id: BulkUploadStatusTypesEnum.unprocessed,
      name: "Unprocessed",
      value: BulkUploadStatusTypesEnum.unprocessed,
    },
    {
      id: BulkUploadStatusTypesEnum.inactive,
      name: "Inactive",
      value: BulkUploadStatusTypesEnum.inactive,
    },
    {
      id: BulkUploadStatusTypesEnum.failed,
      name: "Failed",
      value: BulkUploadStatusTypesEnum.failed,
    },
  ];
}

export function generateBulkUploadSourceTypes(): SelectOptionsInterface[] {
  return [
    {
      id: BulkUploadSourceTypesEnum.phoenix,
      name: "Phoenix",
      value: BulkUploadSourceTypesEnum.phoenix,
    },
    {
      id: BulkUploadSourceTypesEnum.members_portal,
      name: "Members Portal",
      value: BulkUploadSourceTypesEnum.members_portal,
    },
    {
      id: BulkUploadSourceTypesEnum.sb_portal,
      name: "Savings Booster Portal",
      value: BulkUploadSourceTypesEnum.sb_portal,
    },
    {
      id: BulkUploadSourceTypesEnum.ussd,
      name: "Ussd",
      value: BulkUploadSourceTypesEnum.ussd,
    },
    {
      id: BulkUploadSourceTypesEnum.unknown,
      name: "Unknown",
      value: BulkUploadSourceTypesEnum.unknown,
    },
  ];
}

export function generateInfluenceLevelTypes(): SelectOptionsInterface[] {
  return [
    {
      id: InfluenceLevelTypesEnum.low,
      name: influenceLevelTypes.low,
      value: InfluenceLevelTypesEnum.low,
    },
    {
      id: InfluenceLevelTypesEnum.medium,
      name: influenceLevelTypes.medium,
      value: InfluenceLevelTypesEnum.medium,
    },
    {
      id: InfluenceLevelTypesEnum.high,
      name: influenceLevelTypes.high,
      value: InfluenceLevelTypesEnum.high,
    },
  ];
}

export function generateBeneficiaryTypes(): SelectOptionsInterface[] {
  return [
    {
      id: BeneficiaryTypesEnum.individual,
      name: beneficiaryTypes.individual,
      value: BeneficiaryTypesEnum.individual,
    },
    {
      id: BeneficiaryTypesEnum.institution,
      name: beneficiaryTypes.institution,
      value: BeneficiaryTypesEnum.institution,
    },
  ];
}

export function generateContributionTypes(): SelectOptionsInterface[] {
  return [
    {
      id: ContributionTypesEnum.lumpSum,
      name: contributionTypes.lump_sum,
      value: ContributionTypesEnum.lumpSum,
    },
    {
      id: ContributionTypesEnum.monthlyContribution,
      name: contributionTypes.monthly_contribution,
      value: ContributionTypesEnum.monthlyContribution,
    },
    {
      id: ContributionTypesEnum.monthlyAndLump,
      name: contributionTypes.monthly_and_lumpsum,
      value: ContributionTypesEnum.monthlyAndLump,
    },
  ];
}

export function generateTierTypes(): SelectOptionsInterface[] {
  return [
    {
      id: TiersEnum.tier2,
      name: tierTypes.tier_2,
      value: TiersEnum.tier2,
    },
    {
      id: TiersEnum.tier3,
      name: tierTypes.tier_3,
      value: TiersEnum.tier3,
    },
    {
      id: TiersEnum.tier4,
      name: tierTypes.tier_4,
      value: TiersEnum.tier4,
    },
  ];
}

export function generateExclusionTypes(): SelectOptionsInterface[] {
  return [
    {
      id: ExceptionTypesEnum.inclusion,
      name: exceptionTypes.inclusion,
      value: ExceptionTypesEnum.inclusion,
    },
    {
      id: ExceptionTypesEnum.exclusion,
      name: exceptionTypes.exclusion,
      value: ExceptionTypesEnum.exclusion,
    },
  ];
}

export function generateComplaintTypes(): SelectOptionsInterface[] {
  return [
    {
      id: ComplaintsTypesEnum.notAvailable,
      name: complaintTypes.notAvailable,
      value: ComplaintsTypesEnum.notAvailable,
    },
    {
      id: ComplaintsTypesEnum.service,
      name: complaintTypes.service,
      value: ComplaintsTypesEnum.service,
    },
    {
      id: ComplaintsTypesEnum.intermediary,
      name: complaintTypes.intermediary,
      value: ComplaintsTypesEnum.intermediary,
    },
    {
      id: ComplaintsTypesEnum.product,
      name: complaintTypes.product,
      value: ComplaintsTypesEnum.product,
    },
  ];
}

export function generateCompanySegments(): SelectOptionsInterface[] {
  return [
    {
      id: CompanySegmentsEnum.strategic,
      name: companySegments.strategic,
      value: CompanySegmentsEnum.strategic,
    },
    {
      id: CompanySegmentsEnum.niche,
      name: companySegments.niche,
      value: CompanySegmentsEnum.niche,
    },
    {
      id: CompanySegmentsEnum.growth,
      name: companySegments.growth,
      value: CompanySegmentsEnum.growth,
    },
    {
      id: CompanySegmentsEnum.maintenance,
      name: companySegments.maintenance,
      value: CompanySegmentsEnum.maintenance,
    },
    {
      id: CompanySegmentsEnum.red,
      name: companySegments.red,
      value: CompanySegmentsEnum.red,
    },
  ];
}

export function generateContactTypes(): SelectOptionsInterface[] {
  return [
    {
      id: ContactTypesEnum.phone,
      name: contactTypes.phone,
      value: ContactTypesEnum.phone,
    },
    {
      id: ContactTypesEnum.email,
      name: contactTypes.email,
      value: ContactTypesEnum.email,
    },
  ];
}

export function generateTransferTypes(): SelectOptionsInterface[] {
  return [
    {
      id: TransferTypeEnum.transferIn,
      name: transferTypes.transfer_in,
      value: TransferTypeEnum.transferIn,
    },
    {
      id: TransferTypeEnum.transferOut,
      name: transferTypes.transfer_out,
      value: TransferTypeEnum.transferOut,
    },
    {
      id: TransferTypeEnum.transferWithin,
      name: transferTypes.transfer_within,
      value: TransferTypeEnum.transferWithin,
    },
    {
      id: TransferTypeEnum.transferToPreservation,
      name: transferTypes.transfer_to_preservation,
      value: TransferTypeEnum.transferToPreservation,
    },
    {
      id: TransferTypeEnum.transferToEzhome,
      name: transferTypes.transfer_to_ezhome,
      value: TransferTypeEnum.transferToEzhome,
    },
  ];
}

export function generateRedemptionTypes(): SelectOptionsInterface[] {
  return [
    {
      id: RedemptionTypeEnum.percentage,
      name: redemptionTypes.percentage,
      value: RedemptionTypeEnum.percentage,
    },
    {
      id: RedemptionTypeEnum.amount,
      name: redemptionTypes.amount,
      value: RedemptionTypeEnum.amount,
    },
  ];
}

export const STEPS = {
  started: "started",
  opportunitySourceProvided: "opportunity_source_provided",
  opportunityProspectProvided: "opportunity_prospect_provided",
  customerIdentifierProvided: "customer_identifier_provided",
  customerInformationProvided: "customer_information_provided",
  companyQueryProvided: "company_query_provided",
  productProvided: "product_provided",
  referrerProvided: "referrer_provided",
  companyIdentifierProvided: "company_identifier_provided",
  companyInformationProvided: "company_information_provided",
};

export enum ProgressStatuses {
  onboardingInitated = "petra_id_assigned",
  inProgress = "in_progress",
  pendingMatch = "petra_id_assignment_pending",
  incomplete = "incomplete",
  cancelled = "cancelled",
  lead = "lead",
  won = "won",
}

export const notProvidedText = "Not Provided (Optional)";

export const getUserAndTokenFromLocalStorage =
  function (): GetUserTokenResponse {
    const token: string | null = localStorage.getItem("token") as string;

    const user: string | null = localStorage.getItem("user") as string;

    return { token: JSON.parse(token), user: JSON.parse(user) };
  };

export const handleValidationErrors = function (
  err: Record<string, unknown>,
): void {
  console.log("validation error", err);
};

export function filterMethod(
  input: string,
  option?: OptionData | OptionGroupData,
): boolean {
  if (!option) return false;

  const searchText =
    typeof option.children === "string"
      ? option.children
      : typeof option.title === "string"
      ? option.title
      : "";

  if (!searchText) return false;

  return searchText.toLowerCase().indexOf(input.toLowerCase()) >= 0;
}

export function mapAPIErrorToFormError(data: APIError): FieldData {
  return {
    name: data.property,
    errors: [
      `${capitalizeFirstLetter(spaceCamel(data.property))}: ${
        data.description
      }`,
    ],
  };
}

interface ResponseHandlerResponse<Mutation> {
  handled: boolean;
  err: APIError[] | null;
  mutation: Mutation;
}

export function responseHandler<Mutation>(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  response: AxiosResponse<any>,
  mutationName: string,
): ResponseHandlerResponse<Mutation> {
  Sentry.setTag("api-error", `mutation-${mutationName}`);
  if (response && response.data && response.data?.errors) {
    const stringifiedError = JSON.stringify(
      response.data.errors ?? response.data?.data?.errors,
      null,
      4,
    );
    Sentry.captureException(new Error(stringifiedError));
    return {
      handled: true,
      mutation: (response.data?.data?.[mutationName] ?? {}) as Mutation,
      err: null,
    };
  }

  if (
    response &&
    response.data &&
    response.data.data &&
    response.data.data[mutationName] &&
    response.data.data[mutationName].errors &&
    response.data.data[mutationName].errors.length === 0
  ) {
    return {
      handled: false,
      mutation: response.data.data[mutationName],
      err: null,
    };
  } else if (
    response &&
    response.data &&
    response.data.data &&
    response.data.data[mutationName] &&
    response.data.data[mutationName].errors &&
    response.data.data[mutationName].errors.length > 0
  ) {
    Sentry.captureException(
      new Error(
        JSON.stringify(response.data.data[mutationName].errors, null, 4),
      ),
    );
    return {
      handled: false,
      mutation: response.data.data[mutationName],
      err: response.data.data[mutationName].errors,
    };
  } else {
    return { handled: false, mutation: {} as Mutation, err: null };
  }
}

export function responseHandlerForMutationsThatDoNotExposeErrors<Mutation>(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  response: AxiosResponse<any>,
  mutationName: string,
): ResponseHandlerResponse<Mutation> {
  Sentry.setTag("api-error", `mutation-${mutationName}`);
  if (response && response.data && response.data.errors) {
    const stringifiedError = JSON.stringify(response.data.errors, null, 4);
    Sentry.captureException(new Error(stringifiedError));
    return { handled: true, mutation: {} as Mutation, err: null };
  }

  if (
    response &&
    response.data &&
    response.data.data &&
    response.data.data[mutationName] &&
    !response.data.data[mutationName].errors
  ) {
    // sucessful mutation
    return {
      handled: false,
      mutation: response.data.data[mutationName],
      err: null,
    };
  } else {
    return { handled: false, mutation: {} as Mutation, err: null };
  }
}

export const mergeCustomerPaymentInformation = (
  opportunity: Opportunity,
  customer: Customer,
  paymentInfo: UpdatePaymentInformationFnProps,
): Opportunity => {
  const updatedOpportunity: Opportunity = {
    ...opportunity,
    customer: {
      ...customer,
      ...paymentInfo,
    },
  };

  return updatedOpportunity;
};

export const mergeCompanyPaymentInformation = (
  opportunity: Opportunity,
  company: Company,
  paymentInfo: UpdatePaymentInformationFnProps,
): Opportunity => {
  const updatedOpportunity: Opportunity = {
    ...opportunity,
    company: {
      ...company,
      ...paymentInfo,
    },
  };

  return updatedOpportunity;
};

export function filterProductsByProspect(
  opportunity: Opportunity,
  opportunityProspects: OpportunityProspect[],
): Product[] {
  const products: Product[] = opportunityProspects
    .map((propect) => propect.products)
    .flat();
  return products;
}

export const generateNameFromCompany = (
  company: Company | undefined,
): string => {
  let name = "";

  company?.name && (name += company.name);
  company?.petraId && (name += ` - ${company.petraId}`);

  return name;
};

export const generateNameFromCustomer = (
  customer: Customer | undefined,
  includePetraId = true,
): string => {
  let name = "";
  customer?.firstName && (name += customer.firstName);
  customer?.middleName && (name += ` ${customer.middleName}`);
  customer?.lastName && (name += ` ${customer.lastName}`);
  if (includePetraId) {
    customer?.petraId && (name += ` - ${customer.petraId}`);
  }
  return name;
};

export const formatRedemptionPurpose = (purpose: string): string => {
  return purpose.replace(/_/g, " ");
};

export const LOAD_OPPORTUNITY_LIGHT = `
query GetLightweightOpportunity($opportunityId: ID!) {
  opportunity(id: $opportunityId) {
    signedApplicationForm {
      id
      url
    }
    directDebitAuthorizationForm {
      id
      url
    }
    vestingRuleDocument {
      id
      url
    }
    npraForm {
      id
      url
    }
    employeeDatasheet {
      id
      url
    }
  }
}
`;

export function injectKey<T>(elements: T[], key: keyof T): T[] {
  const remodeled: T[] = [];
  for (const element of elements) {
    remodeled.push({
      key: element[key],
      id: element[key],
      name: element[key],
      ...element,
    });
  }
  return remodeled;
}

export function convertCustomersToSelectOptions(
  customers: Customer[] | null | undefined,
): SelectOptionsInterface[] {
  const results: SelectOptionsInterface[] = [];

  if (!customers || !Array.isArray(customers)) return results;

  for (const customer of customers) {
    results.push({
      id: customer.id,
      name: customer.fullname
        ? `${customer.fullname} - ${customer.petraId}`
        : generateNameFromCustomer(customer),
    });
  }

  return results;
}

export const getCompanyPetraId = (
  customer: Customer,
  companyId: string,
): string | undefined => {
  const selectedCompany = customer.employers?.find(
    (emp) => emp.id === companyId,
  );
  const companyPetraIdFromAssociations =
    customer.customerCompanyAssociations?.find(
      (assoc) => assoc.company.id === companyId,
    )?.company.petraId;

  return selectedCompany?.petraId || companyPetraIdFromAssociations;
};

export function convertCompaniesToSelectOptions(
  companies: Company[],
): SelectOptionsInterface[] {
  const results: SelectOptionsInterface[] = [];

  for (const company of companies) {
    results.push({
      id: company.id,
      name: generateNameFromCompany(company),
    });
  }

  return results;
}

export function convertStringArrayToSelectOptions(
  args: string[],
): SelectOptionsInterface[] {
  const results: SelectOptionsInterface[] = [];

  for (const arg of args) {
    results.push({
      id: arg,
      name: arg,
      key: arg,
      value: arg,
    });
  }

  return results;
}

export const generateNameFromPort = (port: Port | null): string => {
  let name = "";

  if (port) {
    const relMan = port.relationshipManager.name.split(" ")[0];
    const relOff = port.relationshipOfficer.name.split(" ")[0];
    name = `${relMan} & ${relOff}`;
  }
  return name;
};

export function convertPortToSelectOptions(
  ports: Port[],
): SelectOptionsInterface[] {
  const results: SelectOptionsInterface[] = [];

  for (const port of ports) {
    results.push({
      id: port.id,
      name: generateNameFromPort(port),
    });
  }
  return results;
}

export function convertUserToSelectOptions(
  users: User[],
): SelectOptionsInterface[] {
  const results: SelectOptionsInterface[] = [];

  for (const user of users) {
    results.push({
      id: user.id,
      name: user.name,
    });
  }
  return results;
}

export function createTransactionFieldsSelectOptions(): SelectOptionsInterface[] {
  const results: SelectOptionsInterface[] = [];
  // eslint-disable-next-line
  Object.entries(TransactionFieldsEnum).map(([key, value]): void => {
    results.push({
      id: key,
      name: value,
    });
  });
  return results;
}

export function errorsHasSignature(
  errors: APIError[],
  signature: string,
): boolean {
  for (const error of errors) {
    if (error.property === signature) {
      return true;
    }
  }
  return false;
}

const AUTOMATIC_TAG_NAMES = ["Active", "Inactive", "Awaiting First Payment", "Kindergarten", "Dormant", "Non-Paying", "Ported Out"];

export function isAutomaticTag(name: string): boolean {
  return AUTOMATIC_TAG_NAMES.some((t) => t.toLowerCase() === name.toLowerCase());
}

export function formatSubscriptionTags(
  tags: SubscriptionTag[],
): SelectOptionsInterface[] {
  return tags.map((tag) => tag.tag).filter((tag) => !isAutomaticTag(tag.name));
}

export function subscriptionInitialTags(tags: SubscriptionTag[]): string[] {
  return tags.map((tag) => tag.tag.id);
}

export function mapSubscriptionToContribution(
  contributions: Contribution[] = [],
  subscriptions: Subscription[] = [],
  schemes: Scheme[] = [],
): ProductContributionSubscriptionMapping[] {
  const resultMapping: ProductContributionSubscriptionMapping[] = [];
  subscriptions.forEach((subscription) => {
    const clientMatchedContributions: Contribution[] = [];
    let result: ProductContributionSubscriptionMapping = {
      clientContributions: [],
      subscription: subscription,
      scheme: {} as Scheme,
    };

    contributions.forEach((contribution) => {
      if (contribution.shareClass === subscription.product.fundCode) {
        clientMatchedContributions.push(contribution);
      }
    });
    result = { ...result, clientContributions: clientMatchedContributions };
    schemes.forEach((scheme) => {
      if (scheme.product === subscription.product.fundCode) {
        result = { ...result, scheme: scheme };
      }
    });
    resultMapping.push(result as ProductContributionSubscriptionMapping);
  });
  return resultMapping;
}

export function formatPayoutAccountLabel(account: Account): string {
  if ("payoutAccount" in account) {
    const payoutAccount = account.payoutAccount;
    return payoutAccount.bankBranch
      ? `${payoutAccount.name} - ${payoutAccount.bankBranch.name} - ${payoutAccount.accountNumber}`
      : payoutAccount.accountType === "cheque"
      ? `${payoutAccount.name} - Cheque`
      : `${payoutAccount.name} - ${payoutAccount.accountNumber}`;
  }

  return account.bankBranch
    ? `${account.name} - ${account.bankBranch.name} - ${account.accountNumber}`
    : account.accountType === "cheque"
    ? `${account.name} - Cheque`
    : `${account.name} - ${account.accountNumber}`;
}

export function formatPayoutAccountAsSelectOptions(
  accounts: Account[],
): SelectOptionsInterface[] {
  const modifiedAccounts: SelectOptionsInterface[] = accounts.map((account) => {
    const id = "payoutAccount" in account ? account.payoutAccount.id : account.id;
    const isVerified =
      "isPayoutAccountVerified" in account
        ? !!account.isPayoutAccountVerified
        : false;

    return { id, name: formatPayoutAccountLabel(account), isVerified };
  });
  return [
    ...modifiedAccounts,
    { id: "new_account", name: "New Redemption Payout Account" },
  ];
}

export function omitValue(items: string[], value: string): string[] {
  return items.filter((item) => item !== value);
}

export function formatSnakeCase(value: string): string {
  return value.split("_").join(" ");
}

export function toSnakeCase(str: string) {
  return str
    .match(/[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\b)|[A-Z]?[a-z]+[0-9]*|[A-Z]|[0-9]+/g)
    ?.map((x) => x.toLowerCase())
    .join("_");
}

export function spaceCamel(str: string) {
  return str.replace(/([a-z])([A-Z])/g, "$1 $2");
}

export function snakeToCamel(str: string): string {
  return str
    .toLowerCase()
    .replace(/([-_][a-z])/g, (group) =>
      group.toUpperCase().replace("-", "").replace("_", ""),
    );
}

export function isEmail(str: string): boolean {
  // Regular expression to check if string is email
  const regexExp =
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/gi;

  return regexExp.test(str);
}

export function dateFormatMonthYear(date: string) {
  const momentDate = moment(date.toString());
  const monthName = momentDate.format("MMMM").substring(0, 3);
  const year = momentDate.year();
  return `${monthName} ${year}`;
}

export const ticketSource = "zammad";

export function generateFirstSelectOption(name: string) {
  return [
    {
      id: "noId",
      name,
      value: undefined,
      key: 0,
    },
  ] as SelectOptionsInterface[];
}

export const badValues = ["", null, undefined];

export const loadSavedFilters = (model: string): IField[] => {
  const savedFields = localStorage.getItem(model);
  if (savedFields) {
    return JSON.parse(savedFields) as IField[];
  } else {
    return [] as IField[];
  }
};

export const getPageDetails = (
  currentDataCount: number,
  totalDataCount: number,
  page: number,
  pageSize: number,
) => {
  if (totalDataCount === 0) {
    return `0 - 0 / 0`;
  }
  const accumulatedNCount = currentDataCount + (page - 1) * pageSize;
  const fromNumber = accumulatedNCount - currentDataCount + 1;

  return `${fromNumber} - ${accumulatedNCount} / ${totalDataCount}`;
};

export const showTotalPagination = (total: number, range: [number, number]): string => {
  return `${range[0]} - ${range[1]} / ${total}`;
};

export const createAbsoluteHttpLink = (url: string): string => {
  return url.includes("http") ? url : `//${url}`;
};

export const generateDisplayErrorMessage = (error: unknown): string => {
  const responseErrors = (
    error as { response?: { data?: { errors?: APIError[] } } }
  )?.response?.data?.errors;
  const apiErrors = Array.isArray(error) ? error : responseErrors;

  if (Array.isArray(apiErrors)) {
    return apiErrors
      .map(mapAPIErrorToFormError)
      .map((errItem) => errItem.errors?.join(". "))
      .join(". ");
  }

  return error instanceof Error ? error.message : "";
};

export const convertSlaToDays = (sla?: number) => {
  if (!sla) return "N/A";
  const days = Math.floor(sla / 9);
  const hours = sla % 9;
  return `${days} day(s) ${hours} hour(s)`;
};

export const filterAccountsByMode = (
  accounts: PaymentAccount[],
  mode: PaymentModeTypesEnum,
): PaymentAccount[] => {
  return accounts.filter((account) => {
    switch (mode) {
      case PaymentModeTypesEnum.cheque:
        return account.payoutAccount.accountType === "cheque";
      case PaymentModeTypesEnum.mobileMoney:
        return account.payoutAccount.accountType === "mm_account";
      case PaymentModeTypesEnum.bankTransfer:
        return account.payoutAccount.accountType === "bank_account";
      default:
        return true;
    }
  });
};

const SAVED_PAYOUT_ACCOUNT_BASE_HIDDEN_FIELDS = [
  "accountType",
  "name",
  "financialInstitutionId",
  "accountNumber",
  "bankAccountType",
  "bankBranchId",
  "savePayoutAccount",
  "accountName",
];

// Whether a saved payout account is a bank account, read from the account
// itself rather than the form's Payment Mode select. Payment Mode is optional
// and offers an "N/A" choice, so keying off it let an operator skip
// verification entirely by never touching that dropdown.
export const isBankPayoutAccount = (
  account: PaymentAccount | undefined,
): boolean =>
  (account?.payoutAccount?.accountType ?? account?.accountType) ===
  PaymentAccountTypesEnum.bankAccount;

export const savedPayoutAccountHiddenFields = (
  account: PaymentAccount | undefined,
  options?: { showThirdParty?: boolean },
): string[] => {
  const canVerifyInPlace =
    isBankPayoutAccount(account) && !!account && !account.isPayoutAccountVerified;

  const hidden = [...SAVED_PAYOUT_ACCOUNT_BASE_HIDDEN_FIELDS];

  // "Third Party?" is normally offered only when creating a new payout account,
  // since a saved account was already vetted when it was saved. The edit
  // redemption page keeps showing it, matching its original behaviour.
  if (!options?.showThirdParty) hidden.push("bypassValidation");

  return canVerifyInPlace ? hidden : [...hidden, "verifyPayoutAccount"];
};

export interface PayoutVerificationGateInput {
  selectedAccount?: PaymentAccount;
  hasPassingValidation: boolean;
  lastVerificationFailure?: PayoutVerificationFailure;
  bypassValidation: boolean;
  clientName: string;
  threshold: number;
}

// Both outcomes block: an unverified saved bank account cannot be submitted,
// whether verification was never attempted or was attempted and failed.
// "Third Party?" is not offered on saved accounts, so the only route for an
// account that legitimately will not match is to re-add it as a new payout
// account with Third Party ticked. The copy below says so.
export const getPayoutVerificationError = ({
  selectedAccount,
  hasPassingValidation,
  lastVerificationFailure,
  bypassValidation,
  clientName,
  threshold,
}: PayoutVerificationGateInput): string | undefined => {
  if (!selectedAccount) return undefined;
  if (!isBankPayoutAccount(selectedAccount)) return undefined;
  if (selectedAccount.isPayoutAccountVerified) return undefined;
  if (bypassValidation) return undefined;
  if (hasPassingValidation) return undefined;

  const label = formatPayoutAccountLabel(selectedAccount);

  if (lastVerificationFailure) {
    return `Account name mismatch on ${label}: client is ${clientName}, bank returned ${
      lastVerificationFailure.bankAccountName
    } (${Math.round(
      lastVerificationFailure.score,
    )}% match, minimum ${threshold}%). If this account belongs to a third party, add it as a new payout account and tick Third Party?.`;
  }

  return `${label} has not been verified. Click Verify to check it against the bank before submitting.`;
};

export const filterUndefinedValues = (obj: { [key: string]: any }) => {
  return Object.fromEntries(
    Object.entries(obj).filter(([key, value]) => value !== undefined),
  );
};

export const generateStateButtonText = (event: string): string => {
  return capitalizeEachWord(formatSnakeCase(event));
};

export const TREASURY_BILL_FUND_CODE = "FI00000006";

export const isTreasuryBillProduct = (
  product?: { fundCode?: string | null } | null,
): boolean => product?.fundCode === TREASURY_BILL_FUND_CODE;

export const capitalizeEachWord = (sentence: string): string => {
  if (!sentence) return sentence;
  return sentence.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
};

/** Labels for uploaded document cards; snake_case values get title case with known-type overrides. */
const UPLOADED_FILE_TYPE_LABELS: Partial<Record<string, string>> = {
  [FileTypes.npraForm]: "NPRA Form",
  [FileTypes.signedApplicationForm]: "Signed Application Form",
  [FileTypes.directDebitAuthorizationForm]: "Direct Debit Authorization Form",
  [FileTypes.employeeDatasheet]: "Employee Data Sheet",
  [FileTypes.vestingRuleDocument]: "Vesting Rule Document",
  [FileTypes.users]: "Users",
};

export function formatUploadedDocumentFileTypeLabel(
  fileType: string | undefined,
): string {
  if (!fileType) return "";
  const mapped = UPLOADED_FILE_TYPE_LABELS[fileType];
  if (mapped) return mapped;
  if (!fileType.includes("_")) {
    return fileType;
  }
  return capitalizeEachWord(formatSnakeCase(fileType));
}

export const getCustomerEmailAddress = (customer: Customer): string => {
  const { customerEmails } = customer;

  if (!customerEmails?.length) {
    return "n/a";
  }

  const primaryActiveEmail = customerEmails.find(
    (email) => email.isActive && email.isPrimary && email.email,
  );
  if (primaryActiveEmail?.email) {
    return primaryActiveEmail.email.address;
  }

  const activeOrPrimaryEmail = customerEmails.find(
    (email) => (email.isActive || email.isPrimary) && email.email,
  );
  if (activeOrPrimaryEmail?.email) {
    return activeOrPrimaryEmail.email.address;
  }

  return "n/a";
};

export const getCustomerPhoneNumber = (customer: Customer): string => {
  const { activeCustomerPhones } = customer;
  if (!activeCustomerPhones?.length) {
    return "n/a";
  }
  return activeCustomerPhones[0].phone.number;
};

export const getCompanyEmail = (company: Company): string => {
  const { companyEmails } = company;

  if (!companyEmails?.length) {
    return "n/a";
  }

  const primaryEmail = companyEmails.find((email) => email.isPrimary);
  if (primaryEmail) {
    return primaryEmail.email.address;
  }

  return companyEmails[0].email.address;
};

export const getCompanyPhoneNumber = (company: Company): string => {
  const { companyPhones } = company;
  if (!companyPhones?.length) {
    return "n/a";
  }
  return companyPhones[0].phone.number;
};

export const getTransferOriginAndDestination = (
  transfer: TransferRequest,
): string => {
  const from = transfer.fromCompanyName || transfer.fromProduct?.name;
  const to = transfer.toCompanyName || transfer.toProduct?.name;

  if (!from && !to) return "N/A";
  if (!to) return from || "N/A";
  if (!from) return to || "N/A";

  return `${from} -> ${to}`;
};

export const isCustomerDetailsPage = (): boolean =>
  !!window.location.pathname.match(/^\/customers\/[^/]+$/);

export const isCompanyDetailsPage = (): boolean =>
  !!window.location.pathname.match(/^\/companies\/[^/]+$/);

export const isProductDurationTenYearsOrMore = (duration: string): boolean => {
  if (!duration) return false;
  const match = duration.match(/(\d+)\s*years?/i);
  if (match && match[1]) {
    const years = parseInt(match[1], 10);
    return years >= 10;
  }
  return false;
};

// Companies carrying any of these tags have nobody left who can endorse an
// employee's redemption, so the redemption authorizer stops being mandatory.
// Kept here rather than in constants.ts, which already imports this module.
export const AUTHORIZER_OPTIONAL_COMPANY_TAGS = [
  "No Employer Endorsement Required",
  "Closed Down",
  "Closed",
];

export const isAuthorizerOptionalForCompany = (
  company?: Pick<Company, "tags">,
): boolean => {
  const optionalTags = AUTHORIZER_OPTIONAL_COMPANY_TAGS.map((name) =>
    name.toLowerCase(),
  );

  // Tag names are backend records, so casing and padding are not guaranteed to
  // match what we hardcode.
  return (company?.tags ?? []).some((tag) =>
    optionalTags.includes((tag?.name ?? "").trim().toLowerCase()),
  );
};

export const displayRedemptionError = (err: any) => {
  if (Array.isArray(err)) {
    const customError = err.find((e) => e.property === "taxWaiverNpraUrl");
    if (customError) {
      message.error("Contribution Verification Document required");
    } else {
      message.error(generateDisplayErrorMessage(err));
    }
  } else {
    message.error(generateDisplayErrorMessage(err));
  }
};

export const extractFileName = (filename: string): string => {
  const nameWithoutExt = filename.replace(/\.[^/.]+$/, "");
  const extension = filename.match(/\.[^/.]+$/)?.[0] || "";
  // Strip the upload timestamp the backend appends: either the current
  // "_YYYY-MM-DD_HH-MM-SS" suffix or the legacy " - <date>" one.
  const baseName = nameWithoutExt
    .split(" - ")[0]
    .replace(/_\d{4}-\d{2}-\d{2}_\d{2}-\d{2}-\d{2}$/, "");
  return baseName + extension;
};

export const decodeFileName = (filename: string): string => {
  try {
    const urlMatch = filename.match(/\/([^/]+)$/);
    if (urlMatch) {
      const encodedName = urlMatch[1];
      const decoded = decodeURIComponent(encodedName);
      return extractFileName(decoded);
    }
    const decoded = decodeURIComponent(filename);
    return extractFileName(decoded);
  } catch {
    return filename;
  }
};

export const filterActiveProducts = <T extends { isActive?: boolean | null }>(
  products?: T[] | null,
): T[] => (products ?? []).filter((product) => product.isActive !== false);

export const filterActiveSubscriptions = <
  T extends { product: { isActive?: boolean | null } },
>(
  subscriptions?: T[] | null,
): T[] =>
  (subscriptions ?? []).filter(
    (subscription) => subscription.product.isActive !== false,
  );

export const STATE_COLORS = {
  // Fixed Income Request states (updated to match new backend states)
  pending_kyc: "orange",
  pending_approval: "purple",
  pending_compliance_review: "pink",
  approved: "green",
  pending_payment_verification: "gold",
  pending_verification: "gold",
  payment_verified: "cyan",
  verified: "cyan",
  cancelled: "red",
  hold: "default",
  purchased: "green",
  rolled_over: "green",
  matures_in_two_weeks: "orange",
  matured: "blue",
  redeemed: "blue",

  // Legacy states (keep for backward compatibility if needed)
  rejected: "volcano",

  // Loan states
  awaiting_application_forms: "orange",
  under_review: "gold",
  pending_ops_processing: "pink",
  processing: "pink",
  pending_ops_approval: "purple",
  sent_to_provider: "blue",
  disbursed: "green",
  accepted: "green",

  // Payment states
  pending: "gold",
  sent_to_TA: "blue",
  completed: "green",
  failed: "red",
  paid: "green",
  not_paid: "red",
  payment_received: "cyan",
  payment_processing: "orange",

  // Redemption states
  pending_vesting_confirmation: "orange",
  processed: "cyan",
  payment_delayed: "orange",
  payments: "green",
  dealing: "blue",
  correspondence: "cyan",
  reversed: "red",
  recalled: "red",
  reissued: "green",
  returned: "blue",

  // Transfer Request States
  awaiting_funds: "gold",
  update_customer: "purple",
  information_received: "cyan",

  // Online Transactions States
  pending_authorizer: "purple",
  authorizer_cancelled: "red",
  expired: "gray",
  initiated: "blue",
  issued: "green",

  // Interactions State
  closed: "red",
  open: "green",
  on_hold: "red",

  // Notification Records States
  delivered: "green",

  // Bulk Uploads States
  unprocessed: "gold",

  // USSD States
  active: "green",
  inactive: "red",
  blocked: "gray",
  "Not Registered": "gold",

  // Opportunities States
  Lead: "red",
  "PetraID Assigned": "blue",
  Won: "green",
  "In Progress": "gold",

  //TA States
  success: "green",
  "reversal initiated": "blue",
} as const;

export const getStateColor = (state: string): string => {
  return STATE_COLORS[state as keyof typeof STATE_COLORS] || "default";
};

export const getStateButtonClass = (state: string): string => {
  const color = getStateColor(state);

  switch (color) {
    case "red":
      return "error-btn";
    case "green":
      return "success-btn";
    case "orange":
      return "warning-btn";
    case "gold":
      return "gold-btn";
    case "pink":
      return "pink-btn";
    case "purple":
      return "primary-btn";
    case "blue":
      return "blue-btn";
    case "volcano":
      return "volcano-btn";
    case "cyan":
      return "cyan-btn";
    default:
      return "warning-button";
  }
};

export const getTransactionFieldValue = (field: any): number | string => {
  if (typeof field === "object" && field !== null) {
    return field.parsedValue ?? field.source ?? field;
  }
  return field;
};

export const formatCurrency = (value: string | number): string => {
  return parseFloat(value.toString()).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export const formatLabel = (key: string): string => {
  return key
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

// Older KYCs stored the institution's id as the bank name.
export const institutionNameFor = (
  bankName: string | undefined,
  institutions: SelectOptionsInterface[],
): string | undefined =>
  institutions.find((institution) => institution.id === bankName)?.name ||
  bankName;

export const convertEnumToSelectOptions = (
  enumValues: string[] = [],
): SelectOptionsInterface[] => {
  return enumValues.map((value) => ({
    id: value,
    name: value
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" "),
    value: value,
  }));
};

export const getRiskColor = (level: string) => {
  switch (level) {
    case "low_risk":
      return "success";
    case "medium_risk":
      return "orange";
    case "high_risk":
      return "error";
    default:
      return "default";
  }
};

export const getRiskLevelDisplay = (level: string) => {
  switch (level) {
    case "low_risk":
      return "Low Risk";
    case "medium_risk":
      return "Medium Risk";
    case "high_risk":
      return "High Risk";
    default:
      return level;
  }
};

export const getNthColumnOffsetLayout = (
  position: number,
  columnsPerRow: number,
  span: number,
): GridColConfig => {
  const columnIndex = position % columnsPerRow;

  return {
    span,
    offset: columnIndex === 0 ? 0 : 1,
  };
};

export const createGridFlow = (columnsPerRow: number, span: number) => {
  let position = 0;

  return () => getNthColumnOffsetLayout(position++, columnsPerRow, span);
};

export const getNumericValue = (val: any): number => {
  if (val === null || val === undefined) return 0;
  if (typeof val === "number") return val;
  if (typeof val === "object" && val.parsedValue !== undefined) {
    return val.parsedValue;
  }
  if (typeof val === "object" && val.source !== undefined) {
    return parseFloat(val.source);
  }
  return parseFloat(val.toString());
};

// Coerces any historical transaction_fields shape to the canonical Record<string, string>.
// The backend now always returns a Hash, but legacy strings and arrays may still
// appear in cached payloads, optimistic-update buffers, and old test fixtures.
export const parseTransactionFields = (
  transactionFields:
    | string
    | Record<string, string>
    | Record<string, string>[]
    | null
    | undefined,
): Record<string, string> => {
  let value: unknown = transactionFields;

  while (typeof value === "string") {
    try {
      value = JSON.parse(value);
    } catch {
      return {};
    }
  }

  if (Array.isArray(value)) {
    return value.reduce<Record<string, string>>((acc, item) => {
      return item && typeof item === "object" ? { ...acc, ...item } : acc;
    }, {});
  }

  if (value && typeof value === "object") {
    return value as Record<string, string>;
  }

  return {};
};

export const cleanStringArray = (arr?: string[] | null): string[] => {
  return arr ? arr.filter((e: string) => e && e.trim() !== "") : [];
};

export const fetchEnumOptions = async (
  query: string,
  options: {
    labelFormatter?: (name: string) => string;
    includeColor?: boolean;
    errorMsg?: string;
  } = {},
): Promise<Array<{ value: string; label: string; color?: string }>> => {
  const {
    labelFormatter = (name: string) => formatSnakeCase(name),
    includeColor = false,
    errorMsg = "Failed to fetch options",
  } = options;

  try {
    const response: AxiosResponse = await request({ query });
    const enumValues = response.data?.data?.__type?.enumValues || [];

    return enumValues.map((ev: any) => ({
      value: ev.name,
      label: ev.description || labelFormatter(ev.name),
      ...(includeColor && { color: getStateColor(ev.name.toLowerCase()) }),
    }));
  } catch (error) {
    message.error(errorMsg);
    Sentry.captureException(error);
    return [];
  }
};

export function convertFileOptionsToSelectOptions(
  args: FileOption[],
): SelectOptionsInterface[] {
  const results: SelectOptionsInterface[] = [];

  for (const arg of args) {
    results.push({
      id: arg.key,
      name: arg.value,
      key: arg.key,
      value: arg.key,
    });
  }

  return results;
}

export function isSavingsBoosterProduct(productName?: string): boolean {
  return /savings booster/i.test(productName ?? "");
}

 export const isSavingsBooster = (subscription: Subscription): boolean =>
    /savings booster/i.test(subscription.product?.name || "");

export function isTierThreeProduct(product?: Product): boolean {
  return product?.productTier?.name === "tier_3";
}
